import { Box, Typography, Paper, Table, TableContainer, Button, CircularProgress } from '@mui/material';
import React from 'react';

import useDownloadDashboard from './useDownloadDashboard';

import RenderIf from '../../renderIf';
import { DashboardHeader, DashboardBody } from '../../shared';
import { CompressionSwitch } from '../../shared/search/components';

const HighlightedText = ({ children }) => {
    return (
        <Box
            component="span"
            sx={{ color: (theme) => theme.palette.primary.main }}
        >
            {children}
        </Box>
    );
};

const DownloadDashboard = () => {
    const {
        isInitialized,
        isDoingAjax,
        isCompressed,
        headers,
        list,
        href,
        sortKey,
        sortDir,
        downloadRef,
        handleSort,
        handleCompression,
        retrieveDownloads,
        handleNav,
    } = useDownloadDashboard();

    React.useEffect(() => {
        retrieveDownloads?.();
    }, []);

    return (
        <Box
            sx={{
                padding: { xl: '1% 9.72% 7.5%', md: '1% 9.72% 10%' },
            }}
        >
            <Box
                component="a"
                hidden
                ref={downloadRef}
                href={href}
            />

            <Typography
                sx={(theme) => {
                    return {
                        fontSize: '2.25rem',
                        fontWeight: 600,
                    };
                }}
            >
                <RenderIf isTrue={!isInitialized}>
                    Initializing <HighlightedText>downloads...</HighlightedText>
                </RenderIf>

                <RenderIf isTrue={isInitialized}>
                    My <HighlightedText>Downloads.</HighlightedText>
                </RenderIf>
            </Typography>

            <RenderIf isTrue={isInitialized}>
                <Paper
                    sx={(theme) => {
                        return {
                            mt: theme.spacing(2),
                            mb: theme.spacing(2),
                            padding: theme.spacing(3),
                            borderRadius: '8px',
                            [theme.breakpoints.down('lg')]: {
                                maxWidth: '75vw',
                            },
                            [theme.breakpoints.down('md')]: {
                                maxWidth: '60vw',
                            },
                            [theme.breakpoints.down('sm')]: {
                                maxWidth: 'unset',
                            },
                        };
                    }}
                >
                    <Box
                        sx={(theme) => {
                            return {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                mb: theme.spacing(3),
                            };
                        }}
                    >
                        <Box
                            sx={(theme) => {
                                return {
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: theme.spacing(1),
                                };
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={retrieveDownloads}
                                disabled={isDoingAjax}
                            >
                                Refresh list
                            </Button>

                            <RenderIf isTrue={isDoingAjax}>
                                <CircularProgress />
                            </RenderIf>
                        </Box>

                        <CompressionSwitch handleSwitch={handleCompression} />
                    </Box>
                    <TableContainer
                        sx={(theme) => {
                            return {
                                maxHeight: '55vh',
                                overflow: 'auto',
                                [theme.breakpoints.down('sm')]: {
                                    maxHeight: '50vh',
                                },
                            };
                        }}
                    >
                        <Table
                            stickyHeader
                            size={isCompressed ? 'small' : 'medium'}
                        >
                            <RenderIf isTrue={headers}>
                                <DashboardHeader
                                    disabled={isDoingAjax}
                                    headers={headers}
                                    handleSort={handleSort}
                                    handleCheckbox={() => null}
                                    allSelected={false}
                                    sortKey={sortKey}
                                    sortDir={sortDir}
                                />
                            </RenderIf>

                            <RenderIf isTrue={headers && list}>
                                <DashboardBody
                                    list={list}
                                    headers={headers}
                                    isFetching={false}
                                    disabled={isDoingAjax}
                                    handleNav={handleNav}
                                    selectedRows={[]}
                                    handleCheckbox={() => null}
                                />
                            </RenderIf>
                        </Table>
                    </TableContainer>
                </Paper>
            </RenderIf>
        </Box>
    );
};

export default DownloadDashboard;

